import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import styled from "@emotion/styled";
import MasterReportFilters, { DEFAULT_FILTERS } from '../components/MasterReportFilters';
import MasterReportSorting from '../components/MasterReportSorting';
import MasterReportPresets from '../components/MasterReportPresets';
import MasterReportResults, { orderReportRows } from '../components/MasterReportResults';
import AddTimeRecordDialog from '../components/AddTimeRecordDialog';
import TaskDetailsDialog from '../components/TaskDetailsDialog';
import { tagDisplayName } from '../components/TagPicker';
import PageHeader from '../components/PageHeader';
import { getMasterReport, getMasterReportTask } from '../redux/ducks/reports';
import { fetchAccountUsers } from '../redux/slices/users';
import { postTime } from '../redux/ducks/time';
import { getSkills } from '../redux/slices/skill';
import { getTask } from '../redux/ducks/task';
import { showAlert } from '../redux/slices/alerts';
import { downloadBlob, toCsv } from '../utils/downloadUtils';
import { addUpTime, convertMinutesToHours } from '../utils/timeUtils';
import { formatDate, formatDateTime, toLocalDay } from '../utils/dateUtils';

const MasterReportContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: theme.breakpoints.values.xl,
    margin: '0 auto',
    mt: theme.spacing(4),
    mb: theme.spacing(4),
}));

const ControlsStack = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const DEFAULT_SORTING = {
    sortBy: "createdAt",
    thenBy: "client",
    sortOrder: "desc",
    groupBy: "project",
};

const CSV_COLUMNS = [
    { header: 'Group', get: (row) => row.groupKey },
    { header: 'Name', get: (row) => row.name },
    { header: 'Type', get: (row) => row.type },
    { header: 'Client', get: (row) => row.client },
    { header: 'Account', get: (row) => row.account },
    { header: 'Department', get: (row) => row.department },
    { header: 'Project', get: (row) => row.project },
    { header: 'Group Name', get: (row) => row.group },
    { header: 'Responsible', get: (row) => row.responsibleUser },
    { header: 'Assignees', get: (row) => (row.assignees || []).join('; ') },
    { header: 'Priority', get: (row) => row.priority },
    { header: 'Starred', get: (row) => (row.starred ? 'Yes' : 'No') },
    { header: 'Start Date', get: (row) => formatDate(row.startDate) },
    { header: 'Due Date', get: (row) => formatDate(row.dueDate) },
    { header: 'Created Date', get: (row) => formatDateTime(row.createdAt) },
    { header: 'Last Activity Date', get: (row) => formatDateTime(row.updatedAt) },
    { header: 'Estimated Hours', get: (row) => row.quotedTime || 0 },
    { header: 'Actual Hours', get: (row) => addUpTime(convertMinutesToHours(row.actualTime)) },
    { header: 'Tags', get: (row) => [...new Set((row.tags || []).map((entry) => tagDisplayName(entry.tag)))].join('; ') },
];

const MasterReport = () => {
    const dispatch = useDispatch();
    const [filterValues, setFilterValues] = useState(DEFAULT_FILTERS);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [sorting, setSorting] = useState(DEFAULT_SORTING);
    const [data, setData] = useState([]);
    const skills = useSelector(state => state.skills);
    const [isLoading, setIsLoading] = useState(false);
    const [timeDialogOpen, setTimeDialogOpen] = useState(false);
    const [timeDialogObject, setTimeDialogObject] = useState({});
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);

    // Always a fresh object so re-running an unchanged search still refetches
    const handleSearch = () => {
        setFilters({ ...filterValues })
    }

    const handleReset = () => {
        setFilterValues({ ...DEFAULT_FILTERS })
        setFilters({ ...DEFAULT_FILTERS })
    }

    const handleSortingChange = (key) => (value) => {
        setSorting((prev) => ({ ...prev, [key]: value }))
    }

    const handleExportCsv = () => {
        if (data.length === 0) {
            dispatch(showAlert({ severity: 'info', message: 'There is nothing to export — run a search that returns results first.' }));
            return;
        }
        const rows = orderReportRows(data, sorting);
        const stamp = toLocalDay();
        downloadBlob(toCsv(rows, CSV_COLUMNS), `master-report-${stamp}.csv`);
    }

    const handleApplyPreset = (preset) => {
        const presetFilters = { ...DEFAULT_FILTERS, ...(preset.filters || {}) };
        setFilterValues(presetFilters)
        setFilters(presetFilters)
        setSorting({ ...DEFAULT_SORTING, ...(preset.sorting || {}) })
    }

    useEffect(() => {
        if (skills.length === 0) {
            dispatch(getSkills());
        }
    }, [skills, dispatch]);

    const handleAddTime = (timeData) => {
        dispatch(postTime(timeDialogObject.type, timeDialogObject._id, timeData, (response) => {
            if (response && response.timeRecord) {
                const updatedData = data.map(item => {
                    if (item._id === timeDialogObject._id) {
                        let updatedActualTime;
                        
                        if (item.actualTime !== undefined) {
                            if (item.actualTime && typeof item.actualTime === 'object') {
                                const keys = Object.keys(item.actualTime);
                                if (keys.length > 0) {
                                    const key = keys[0];
                                    if (typeof item.actualTime[key] === 'number') {
                                        updatedActualTime = {
                                            ...item.actualTime,
                                            [key]: item.actualTime[key] + response.timeRecord.time
                                        };
                                    } else {
                                        updatedActualTime = { Hourly: response.timeRecord.time };
                                    }
                                } else {
                                    updatedActualTime = { Hourly: response.timeRecord.time };
                                }
                            } else if (typeof item.actualTime === 'number') {
                                updatedActualTime = item.actualTime + response.timeRecord.time;
                            } else {
                                updatedActualTime = { Hourly: response.timeRecord.time };
                            }
                        } else {
                            updatedActualTime = { Hourly: response.timeRecord.time };
                        }
                        
                        return {
                            ...item,
                            actualTime: updatedActualTime
                        };
                    }
                    return item;
                });
                
                setData(updatedData);
            }
        }))
    }

    const handleTimeDialogClose = () => {
        setTimeDialogOpen(false)
        setTimeDialogObject({})
    }

    const handleAddTimeClick = (item) => {
        setTimeDialogOpen(true)
        setTimeDialogObject(item)
    }

    const handleTaskDialogClose = () => {
        dispatch(getMasterReportTask(selectedTask._id, (result) => {
            if (result && result.task) {
                const updatedData = data.map(item => {
                    if (item._id === selectedTask._id) {
                        return result.task;
                    }
                    return item;
                });
                setData(updatedData);
            }
        }))
        setTaskDialogOpen(false)
        setSelectedTask(null)
    }

    const handleTaskEditClick = (task) => {
        dispatch(getTask(task._id, (result) => {
            if (result) {
                setSelectedTask(result)
            }
        }))
        setTaskDialogOpen(true);
    }

    useEffect(() => {
        if (timeDialogObject.accountId) {
            dispatch(fetchAccountUsers({ accountId: timeDialogObject.accountId}))
        }
    }, [timeDialogObject.accountId, dispatch])

    useEffect(() => {
        if (Object.keys(filters).length === 0) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const usedFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => 
                value !== null && value !== undefined && value !== ""
            )
        );
        if (Array.isArray(usedFilters.tags)) usedFilters.tags = usedFilters.tags.map((entry) => entry.tag._id);
        dispatch(getMasterReport(usedFilters, (data) => {
            setData(Array.isArray(data) ? data : []);
            setIsLoading(false)
        }))

    }, [filters, dispatch])

    return (
        <MasterReportContainer>
            <PageHeader title="Master Report" />
            <ControlsStack>
                <MasterReportPresets filters={filterValues} sorting={sorting} onApply={handleApplyPreset} />
                <MasterReportFilters
                    values={filterValues}
                    onValuesChange={setFilterValues}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />
                <MasterReportSorting
                    sortBy={sorting.sortBy}
                    thenBy={sorting.thenBy}
                    sortOrder={sorting.sortOrder}
                    groupBy={sorting.groupBy}
                    onSortingChange={handleSortingChange("sortBy")}
                    onSecondarySortingChange={handleSortingChange("thenBy")}
                    onGroupByChange={handleSortingChange("groupBy")}
                    onSortOrderChange={handleSortingChange("sortOrder")}
                    onExport={handleExportCsv}
                />
            </ControlsStack>
            <MasterReportResults
                data={data}
                isLoading={isLoading}
                sortBy={sorting.sortBy}
                sortOrder={sorting.sortOrder}
                thenBy={sorting.thenBy}
                groupBy={sorting.groupBy}
                onAddTimeClick={handleAddTimeClick}
                onTaskEditClick={handleTaskEditClick}
                onCompleteRefresh={handleSearch}
            />
            <AddTimeRecordDialog open={timeDialogOpen} onClose={handleTimeDialogClose} onAddTime={handleAddTime} accountId={timeDialogObject.accountId} />
            {selectedTask && taskDialogOpen && (
                <TaskDetailsDialog open={taskDialogOpen} task={selectedTask} onClose={handleTaskDialogClose} editMode={true} />
            )}
        </MasterReportContainer>
    );
};

export default MasterReport;